import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { Box, CircularProgress, IconButton, InputBase, Tooltip, Typography, keyframes } from "@mui/material";
import {
  CHAT_MAX_FILE_BYTES,
  CHAT_MENTION_ALL_ID,
  CHAT_MENTION_MAX,
  CHAT_MESSAGE_MAX_LENGTH,
  formatBytes,
  isPreviewableImageMime,
  type ChatAttachment,
  type ChatMention,
} from "@xop/dashboard-shared";
import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";

import { toast } from "../../components/ui/use-toast.js";
import { useUploadAttachment, useUploadVoiceNote } from "../../lib/chat/hooks.js";
import { useSpeechToText } from "../../lib/useSpeechToText.js";
import { useVoiceRecorder } from "../../lib/useVoiceRecorder.js";
import { EmojiPopover } from "./chat-composer/EmojiPopover.js";
import { MentionPicker } from "./chat-composer/MentionPicker.js";
import { detectMention, filterMentionCandidates } from "./chat-composer/mentionUtils.js";
import { StagedAttachment } from "./chat-composer/StagedAttachment.js";
import { VoiceRecorderBar } from "./chat-composer/VoiceRecorderBar.js";

const micPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.45); }
  70% { box-shadow: 0 0 0 8px rgba(244, 67, 54, 0); }
  100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
`;

interface Staged {
  att: ChatAttachment;
  /** Local object URL for an image preview, revoked on clear. */
  previewUrl: string | null;
}

/**
 * WhatsApp-style composer: emoji + attach buttons, a pill text field (Enter
 * sends, Shift+Enter newlines), and a round green send. A picked file is
 * uploaded immediately and shown as a staged chip; sending links it to the
 * message (with an optional caption). `onSend` carries the attachment id.
 *
 * This file owns the composer state + flows (value, @-mention reconciliation,
 * attachment staging, dictation, send). The presentational pieces live under
 * `chat-composer/`: the @-picker dropdown (`MentionPicker`), the staged-file
 * chip (`StagedAttachment`), and the quick-emoji grid (`EmojiPopover`); the
 * pure caret/ranking helpers in `mentionUtils`.
 */
export function ChatComposer({
  onSend,
  onType,
  disabled = false,
  placeholder = "Type a message",
  incomingFile = null,
  onIncomingFileHandled,
  mentionCandidates = [],
  onCall,
  replyTo = null,
  onCancelReply,
}: {
  onSend: (body: string, attachmentId?: string, mentionIds?: number[]) => void;
  onType?: () => void;
  disabled?: boolean;
  placeholder?: string;
  /** A file handed in from outside (a drag-drop / paste on the conversation
   * pane) to stage exactly as if it were picked via the attach button. */
  incomingFile?: File | null;
  onIncomingFileHandled?: () => void;
  /** Teammates who can be @-mentioned here (room → everyone, DM → the other
   * party). Empty ⇒ the @-picker never opens. */
  mentionCandidates?: readonly ChatMention[];
  /** Start a 1:1 call with the conversation peer. Provided only for DMs when
   * calling is enabled — the Call button renders in the toolbar next to the
   * dictation mic (a phone, clearly distinct from voice-to-text). */
  onCall?: () => void;
  /** When set, the composer shows a "Replying to …" banner above the input
   * (WhatsApp-style). The reply target id is threaded through `onSend` by the
   * parent; the composer just displays + lets you cancel it. */
  replyTo?: { sender_name: string; preview: string; deleted?: boolean } | null;
  /** Clear the active reply (the banner's × button + Escape). */
  onCancelReply?: () => void;
}): JSX.Element {
  const [value, setValue] = useState("");
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const [staged, setStaged] = useState<Staged | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const upload = useUploadAttachment();

  // @-mention picker. `mention` tracks the active `@query` span (or null); the
  // picker filters `mentionCandidates` by it. `pendingMentions` remembers which
  // ids were inserted, reconciled against the final body on send (deleting the
  // text drops the mention). See docs/chat.md.
  const [mention, setMention] = useState<{ at: number; query: string } | null>(null);
  const [mentionIdx, setMentionIdx] = useState(0);
  const pendingMentions = useRef<Map<number, string>>(new Map());
  const mentionMatches =
    mention && mentionCandidates.length > 0
      ? filterMentionCandidates(mentionCandidates, mention.query)
      : [];
  const pickerOpen = mention !== null && mentionMatches.length > 0;

  // Voice-to-text: dictated phrases append to the message; the mic only shows
  // where the browser supports the Web Speech API (feature-detected).
  const speech = useSpeechToText({
    onResult: (text) => {
      setValue((v) => (v && !/\s$/.test(v) ? `${v} ${text}` : `${v}${text}`));
      onType?.();
    },
    onError: (code) => {
      const blocked = code === "not-allowed" || code === "service-not-allowed";
      toast({
        title: blocked ? "Microphone blocked" : "Voice input error",
        description: blocked
          ? "Allow microphone access to dictate messages."
          : "Could not capture audio. Try again.",
        severity: "error",
      });
    },
  });

  // Voice message: record audio and send it as an attachment (distinct from
  // dictation above — this sends the audio itself, WhatsApp-style). The button
  // only shows where MediaRecorder is supported (feature-detected).
  const voiceUpload = useUploadVoiceNote();
  const [voiceUploading, setVoiceUploading] = useState(false);
  const recorder = useVoiceRecorder({
    onError: (code) => {
      toast({
        title: code === "not-allowed" ? "Microphone blocked" : "Recording error",
        description:
          code === "not-allowed"
            ? "Allow microphone access to record a voice message."
            : "Could not record audio. Try again.",
        severity: "error",
      });
    },
  });

  const trimmed = value.trim();
  const canSend =
    !disabled &&
    !upload.isPending &&
    trimmed.length <= CHAT_MESSAGE_MAX_LENGTH &&
    (trimmed.length > 0 || staged !== null);
  // WhatsApp behaviour: an empty composer offers the voice mic in the send
  // slot; once there's text or a staged file, that slot is the Send button.
  const showVoiceMic =
    recorder.supported && !disabled && trimmed.length === 0 && staged === null && !speech.listening;

  function startRecording(): void {
    // The voice note takes the mic over — DROP any in-flight dictation
    // recording (don't transcribe it into a composer the user is abandoning).
    if (speech.listening) speech.cancel();
    void recorder.start();
  }

  function cancelRecording(): void {
    recorder.cancel();
  }

  async function sendRecording(): Promise<void> {
    setVoiceUploading(true);
    const rec = await recorder.stop();
    if (!rec) {
      setVoiceUploading(false);
      return;
    }
    const ext = rec.mime.includes("mp4")
      ? "m4a"
      : rec.mime.includes("ogg")
        ? "ogg"
        : "webm";
    const file = new File([rec.blob], `voice-message.${ext}`, { type: rec.mime });
    voiceUpload.mutate(file, {
      onSuccess: (att) => {
        onSend("", att.id);
        setVoiceUploading(false);
      },
      onError: () => {
        toast({
          title: "Send failed",
          description: "Could not send the voice message.",
          severity: "error",
        });
        setVoiceUploading(false);
      },
    });
  }

  useEffect(() => {
    // Revoke any pending preview URL on unmount.
    return () => {
      if (staged?.previewUrl) URL.revokeObjectURL(staged.previewUrl);
    };
  }, [staged]);

  // Focus the input the moment a reply starts, so you can type the reply right
  // away (the affordance is on a message bubble, away from the composer).
  useEffect(() => {
    if (replyTo) inputRef.current?.focus();
  }, [replyTo]);

  function clearStaged(): void {
    setStaged((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  }

  function submit(): void {
    if (!canSend) return;
    // Sending mid-dictation means "send what's visible": CANCEL (not stop) so
    // the fallback recording is discarded rather than transcribed into an
    // already-sent, cleared composer moments later.
    if (speech.listening) speech.cancel();
    // Reconcile pending mentions against the final body — an inserted @name the
    // user later deleted is no longer a mention. Cap defensively (server caps
    // too), but sort the `@channel` sentinel to the FRONT first so a message
    // that also names 20 people can't slice the broadcast off. Pass mention_ids
    // ONLY when present so a plain send stays 2-arg.
    const mentionIds = [...pendingMentions.current.entries()]
      .filter(([, name]) => trimmed.includes(`@${name}`))
      .map(([id]) => id)
      .sort((a, b) => (a === CHAT_MENTION_ALL_ID ? -1 : b === CHAT_MENTION_ALL_ID ? 1 : 0))
      .slice(0, CHAT_MENTION_MAX);
    if (mentionIds.length > 0) onSend(trimmed, staged?.att.id, mentionIds);
    else onSend(trimmed, staged?.att.id);
    setValue("");
    clearStaged();
    setMention(null);
    pendingMentions.current.clear();
  }

  // Re-detect the active `@query` after any edit to the field (typing, delete,
  // dictation), given the caret position.
  function refreshMention(text: string, caret: number): void {
    setMention(detectMention(text, caret));
    setMentionIdx(0);
  }

  // Replace the active `@query` span with the chosen teammate's `@Name `, and
  // remember the id for send-time reconciliation.
  function pickMention(c: ChatMention): void {
    if (!mention) return;
    const caret = mention.at + 1 + mention.query.length;
    const next = `${value.slice(0, mention.at)}@${c.display_name} ${value.slice(caret)}`;
    pendingMentions.current.set(c.user_id, c.display_name);
    setValue(next);
    setMention(null);
    setMentionIdx(0);
    onType?.();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    if (pickerOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIdx((i) => (i + 1) % mentionMatches.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIdx((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pickMention(mentionMatches[mentionIdx] ?? mentionMatches[0]!);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMention(null);
        return;
      }
    }
    // Escape cancels an in-progress reply (when the @-picker isn't open).
    if (e.key === "Escape" && replyTo) {
      e.preventDefault();
      onCancelReply?.();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  // Stage a file (from the attach button OR a drag-drop/paste): size-check,
  // upload, then hold it as the pending attachment with an image preview.
  function stageFile(file: File): void {
    if (file.size > CHAT_MAX_FILE_BYTES) {
      toast({
        title: "File too large",
        description: `Max ${formatBytes(CHAT_MAX_FILE_BYTES)} per file.`,
        severity: "error",
      });
      return;
    }
    clearStaged();
    upload.mutate(file, {
      onSuccess: (att) => {
        setStaged({
          att,
          previewUrl: isPreviewableImageMime(file.type) ? URL.createObjectURL(file) : null,
        });
      },
      onError: () => {
        toast({ title: "Upload failed", description: "Could not upload the file.", severity: "error" });
      },
    });
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (file) stageFile(file);
  }

  // A file dropped/pasted onto the conversation pane is staged here so the
  // attach button + drag-drop share one path. (eslint dep: fire only on a new
  // incomingFile — stageFile is stable enough for this trigger.)
  useEffect(() => {
    if (!incomingFile) return;
    stageFile(incomingFile);
    onIncomingFileHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingFile]);

  function insertEmoji(emoji: string): void {
    setValue((v) => v + emoji);
    setEmojiAnchor(null);
  }

  return (
    <Box sx={{ bgcolor: "chat.headerBg", position: "relative" }}>
      {replyTo && (
        <Box
          data-testid="chat-reply-banner"
          sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, pt: 1.25, pb: 0.25 }}
        >
          <ReplyRoundedIcon sx={{ fontSize: 18, color: "chat.accent", flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0, borderLeft: 3, borderColor: "chat.accent", pl: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "chat.accent" }}>
              Replying to {replyTo.sender_name}
            </Typography>
            <Typography
              noWrap
              sx={{
                fontSize: 12.5,
                color: "text.secondary",
                ...(replyTo.deleted ? { fontStyle: "italic" } : {}),
              }}
            >
              {replyTo.deleted ? "Deleted message" : replyTo.preview}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="Cancel reply"
            data-testid="chat-cancel-reply"
            onClick={() => onCancelReply?.()}
            sx={{ color: "chat.meta", flexShrink: 0 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {pickerOpen && (
        <MentionPicker
          matches={mentionMatches}
          activeIdx={mentionIdx}
          onPick={pickMention}
          onHover={setMentionIdx}
        />
      )}

      <StagedAttachment staged={staged} uploading={upload.isPending} onClear={clearStaged} />

      {speech.listening && (
        <Box
          data-testid="chat-listening"
          sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, pt: 1.5, color: "error.main" }}
        >
          <GraphicEqRoundedIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Listening…</Typography>
          {speech.interim && (
            <Typography noWrap sx={{ fontSize: 13, color: "text.secondary", fontStyle: "italic" }}>
              {speech.interim}
            </Typography>
          )}
        </Box>
      )}

      {/* The recorded-fallback clip is at the server transcriber (only ever
          shown when the live engine produced nothing — see useSpeechToText). */}
      {speech.transcribing && !speech.listening && (
        <Box
          data-testid="chat-transcribing"
          sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, pt: 1.5, color: "text.secondary" }}
        >
          <CircularProgress size={14} thickness={5} />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Transcribing…</Typography>
        </Box>
      )}

      {recorder.recording || voiceUploading ? (
        <VoiceRecorderBar
          elapsedMs={recorder.elapsedMs}
          uploading={voiceUploading}
          onCancel={cancelRecording}
          onSend={() => void sendRecording()}
        />
      ) : (
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, px: 1.5, py: 1 }}>
        {/* Call — FAR LEFT (a phone; distinct from the in-field dictation icon). */}
        {onCall && (
          <Tooltip title="Call">
            <IconButton
              aria-label="Start call"
              data-testid="chat-call"
              onClick={onCall}
              sx={{ color: "chat.meta", mb: 0.25 }}
            >
              <CallRoundedIcon />
            </IconButton>
          </Tooltip>
        )}

        <input
          ref={fileInput}
          type="file"
          hidden
          data-testid="chat-file-input"
          onChange={onPickFile}
        />

        {/* The input pill — emoji / attach / dictate live INSIDE it, on the right. */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "flex-end",
            gap: 0.25,
            bgcolor: "background.paper",
            borderRadius: 4,
            pl: 1.75,
            pr: 0.5,
            py: 0.5,
            boxShadow: 1,
          }}
        >
          <InputBase
            inputRef={inputRef}
            value={value}
            onChange={(e) => {
              const text = e.target.value;
              setValue(text);
              refreshMention(text, e.target.selectionStart ?? text.length);
              if (text.trim()) onType?.();
            }}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            multiline
            maxRows={6}
            fullWidth
            inputProps={{ "aria-label": "Message", "data-testid": "chat-composer-input" }}
            sx={{ flex: 1, minWidth: 0, fontSize: 14, py: 0.75 }}
          />
          <Tooltip title="Emoji">
            <IconButton
              size="small"
              aria-label="Insert emoji"
              onClick={(e: MouseEvent<HTMLButtonElement>) => setEmojiAnchor(e.currentTarget)}
              sx={{ color: "chat.meta" }}
            >
              <EmojiEmotionsOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Attach file">
            <span style={{ display: "inline-flex" }}>
              <IconButton
                size="small"
                aria-label="Attach file"
                data-testid="chat-attach"
                onClick={() => fileInput.current?.click()}
                disabled={disabled || upload.isPending}
                sx={{ color: "chat.meta" }}
              >
                <AttachFileRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {speech.supported && (
            <Tooltip title="Dictate (voice to text)">
              <span style={{ display: "inline-flex" }}>
                <IconButton
                  size="small"
                  aria-label={speech.listening ? "Stop voice input" : "Start voice input"}
                  aria-pressed={speech.listening}
                  data-testid="chat-mic"
                  onClick={() => speech.toggle()}
                  disabled={disabled || speech.transcribing}
                  sx={{
                    color: speech.listening ? "error.main" : "chat.meta",
                    ...(speech.listening ? { animation: `${micPulse} 1.6s infinite` } : {}),
                  }}
                >
                  {speech.listening ? <GraphicEqRoundedIcon fontSize="small" /> : <RecordVoiceOverRoundedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        <EmojiPopover anchorEl={emojiAnchor} onClose={() => setEmojiAnchor(null)} onPick={insertEmoji} />

        {/* FAR RIGHT — an empty composer offers the voice-message mic
            (WhatsApp); text or a staged file turns it into Send. */}
        {showVoiceMic ? (
          <Tooltip title="Record voice message">
            <IconButton
              onClick={startRecording}
              aria-label="Record voice message"
              data-testid="chat-voice-record"
              sx={{
                bgcolor: "chat.accent",
                color: "#fff",
                width: 44,
                height: 44,
                flexShrink: 0,
                "&:hover": { bgcolor: "chat.accent", filter: "brightness(0.92)" },
              }}
            >
              <MicRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton
            onClick={submit}
            disabled={!canSend}
            aria-label="Send message"
            data-testid="chat-send"
            sx={{
              bgcolor: "chat.accent",
              color: "#fff",
              width: 44,
              height: 44,
              flexShrink: 0,
              "&:hover": { bgcolor: "chat.accent", filter: "brightness(0.92)" },
              "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" },
            }}
          >
            <SendRoundedIcon />
          </IconButton>
        )}
      </Box>
      )}
    </Box>
  );
}
